import { Tabs } from 'antd';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

const { TabPane } = Tabs;

const videos = [
  { key: '1', label: '1. Begin', src: '/assets/walk-through/begin_enrollment.mp4' },
  { key: '2', label: '2. Submit', src: '/assets/walk-through/submit_details.mp4' },
  { key: '3', label: '3. Trusted', src: '/assets/walk-through/certificate_issued.mp4' },
];

const EnrollmentVideoSection = () => {
  const [activeTab, setActiveTab] = useState('1');
  const videoRefs = useRef({});

  useEffect(() => {
    // Pause all videos when tab changes
    Object.values(videoRefs.current).forEach((videoEl) => {
      if (videoEl) {
        videoEl.pause();
      }
    });

    // Play the active video
    const activeVideo = videoRefs.current[activeTab];
    if (activeVideo) {
      activeVideo.currentTime = 0;
      const playPromise = activeVideo.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.log('Auto-play prevented by browser', error);
        });
      }
    }
  }, [activeTab]);

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  return (
    <motion.div
      className="enrollment-video-tabs"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
    >
      <div className="tabs-wrapper">
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          centered
          size="large"
          className="enrollment-tabs"
          tabBarGutter={24}
        >
          {videos.map((video) => (
            <TabPane
              key={video.key}
              tab={
                <div className="tab-label">
                  <span>{video.label}</span>
                </div>
              }
            >
              <div className="video-container">
                <div className="video-inner-container">
                  <video
                    key={video.src}
                    ref={(el) => {
                      if (el) videoRefs.current[video.key] = el;
                    }}
                    src={video.src}
                    autoPlay={activeTab === video.key}
                    loop
                    muted
                    playsInline
                    controls={false}
                    className="enrollment-video"
                  />
                  <div className="video-overlay"></div>
                  <div className="video-frame"></div>
                </div>
              </div>
            </TabPane>
          ))}
        </Tabs>
      </div>

      <style jsx>{`
        .tabs-wrapper {
          position: relative;
          z-index: 25;
          padding: 10px 0;
        }
        .enrollment-video-tabs {
          margin: 3rem 0;
          text-align: center;
        }

        .video-container {
          max-width: 1280px;
          width: 100%;
          margin: 0 auto;
          position: relative;
          padding: 20px;
        }

        .video-inner-container {
          position: relative;
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
          transition: all 0.5s ease;
          background: white;
          border: 1px solid rgba(0, 0, 0, 0.06);
        }

        .video-inner-container:hover {
          box-shadow: 0 25px 50px rgba(36, 123, 254, 0.12);
          border-color: rgba(36, 123, 254, 0.1);
        }

        .enrollment-video {
          width: 100%;
          height: auto;
          display: block;
          transition: transform 0.3s ease;
        }

        .video-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            to bottom,
            rgba(255, 255, 255, 0.02) 0%,
            rgba(255, 255, 255, 0) 70%,
            rgba(0, 0, 0, 0.05) 100%
          );
          pointer-events: none;
        }

        .video-frame {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border-radius: 24px;
          border: 1px solid rgba(0, 0, 0, 0.05);
          box-shadow: inset 0 0 20px rgba(0, 0, 0, 0.03);
          pointer-events: none;
        }

        :global(.enrollment-tabs) {
          width: 100%;
          max-width: 100%;
          margin: 0 auto 2.5rem;
          z-index: 20;
          position: relative;
        }

        :global(.enrollment-tabs .ant-tabs-nav) {
          margin-bottom: 0;
          position: relative;
          z-index: 30;
        }

        :global(.enrollment-tabs .ant-tabs-nav::before) {
          display: none;
        }

        :global(.enrollment-tabs .ant-tabs-tab) {
          margin: 0 16px;
          padding: 10px 0;
          transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          opacity: 0.8;
          position: relative;
          z-index: 10;
        }

        :global(.tab-label) {
          position: relative;
          display: inline-block;
          padding: 14px 26px;
          border-radius: 50px;
          background-color: #f5f5f5;
          transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          font-weight: 500;
          font-size: 16px;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
          border: 1px solid rgba(0, 0, 0, 0.04);
          white-space: nowrap;
          position: relative;
          z-index: 5;
        }

        :global(.ant-tabs-tab-active .tab-label) {
          background-color: #247bfe;
          color: white;
          font-weight: 600;
          transform: scale(1.05);
          box-shadow: 0 8px 16px rgba(36, 123, 254, 0.2);
          border-color: #247bfe;
        }

        :global(.ant-tabs-tab:hover .tab-label) {
          opacity: 1;
          transform: translateY(-2px);
        }

        :global(.ant-tabs-tab-active:hover .tab-label) {
          transform: scale(1.05) translateY(-2px);
        }

        :global(.enrollment-tabs .ant-tabs-tab-active) {
          opacity: 1;
        }

        :global(.enrollment-tabs .ant-tabs-ink-bar) {
          display: none;
        }

        @media (max-width: 1340px) {
          .video-container {
            max-width: 100%;
            padding: 10px 20px;
          }

          .enrollment-video-tabs {
            padding: 0 10px;
          }
        }

        @media (max-width: 767px) {
          .video-container {
            padding: 10px;
          }

          :global(.tab-label) {
            padding: 8px 16px;
            font-size: 14px;
          }

          :global(.enrollment-tabs .ant-tabs-tab) {
            margin: 0 4px;
          }

          .tabs-wrapper {
            padding: 15px 0 20px;
          }

          :global(.enrollment-tabs .ant-tabs-nav) {
            margin-bottom: 24px;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default EnrollmentVideoSection;
